import { NumberFormatter } from "@mantine/core";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import * as R from "ramda";
import React from "react";
import SimpleStampCheck from "../../../../../../components/SimpleStampCheck/SimpleStampCheck";
import { CellEditFormatter } from "../../../../../../components/columnGlobal";
import { formatNumberNb } from "../../../../../utils";
import { dateExportFormatter } from "../issueTableFormatters";
import { IssueTableColumnContext } from "./issueTableColumnContext";

const isLockedAttributeCellEditable = (
  params: { data?: { _id?: string } | null; colDef?: { field?: string } },
  columnContext: IssueTableColumnContext,
  isLocked?: boolean,
) => {
  if (isLocked !== true) {
    return true;
  }

  const issueId = params.data?._id;
  const field = params.colDef?.field;
  if (issueId == null || field == null) {
    return false;
  }

  return columnContext.isOtherAttributeCellUnlocked(issueId, field);
};

const LockedOtherAttributeCell: React.FC<{
  children: React.ReactNode;
  columnContext: IssueTableColumnContext;
  isLocked?: boolean;
  params: ICellRendererParams;
}> = ({ children, columnContext, isLocked, params }) => {
  const issueId = params.data?._id;
  const field = params.colDef?.field;
  const isUnlocked =
    isLocked !== true || issueId == null || field == null
      ? true
      : columnContext.isOtherAttributeCellUnlocked(issueId, field);

  const refreshCurrentCell = () => {
    window.setTimeout(() => {
      params.api.refreshCells({
        columns: field != null ? [field] : undefined,
        force: true,
        rowNodes: [params.node],
      });
    }, 0);
  };

  const toggleLockState = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (issueId == null || field == null) {
      return;
    }

    if (isUnlocked) {
      columnContext.lockOtherAttributeCell(issueId, field);
      refreshCurrentCell();
      return;
    }

    columnContext.unlockOtherAttributeCell(issueId, field);
    refreshCurrentCell();

    if (params.node.rowIndex == null) {
      return;
    }

    window.setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.node.rowIndex!,
        colKey: field,
      });
    }, 0);
  };

  return (
    <div className="d-flex align-items-center h-100 w-100" style={{ gap: 6 }}>
      {isLocked === true && (
        <button
          type="button"
          title={isUnlocked ? "Lås feltet igjen" : "Lås opp for redigering"}
          aria-label={isUnlocked ? "Lås feltet igjen" : "Lås opp for redigering"}
          onClick={toggleLockState}
          className="border-0 bg-transparent p-0"
          style={{
            color: isUnlocked ? "var(--mantine-color-green-7)" : "var(--mantine-color-gray-6)",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          <i className={`fa fa-fw ${isUnlocked ? "fa-unlock" : "fa-lock"}`} />
        </button>
      )}
      <div className="flex-grow-1 overflow-hidden">{children}</div>
    </div>
  );
};

export const getAgGridOtherAttributeColumns = ({
  columnContext,
  isTaskColumns,
  otherAttributesConfig,
}: {
  columnContext: IssueTableColumnContext;
  isTaskColumns?: boolean;
  otherAttributesConfig: IOtherAttributeConfigApi;
}): ColDef<any>[] => {
  const otherAttributeColumns: ColDef<any>[] = [];
  const otherAttributesEntries = Object.entries(otherAttributesConfig ?? {}).map(([key, value]) => ({
    key,
    ...value,
  }));

  const groupedBySection = R.groupBy((item) => item.section, otherAttributesEntries);
  Object.entries(groupedBySection).map(([sectionTitle, attributesInSection]) => {
    attributesInSection
      .sort((a, b) => a.order - b.order)
      .forEach((attribute) => {
        if (attribute.isActive !== true) {
          return;
        }

        const editableWhenUnlocked = (params) =>
          isLockedAttributeCellEditable(params, columnContext, attribute.locked === true);
        const renderLockedCell = (params: ICellRendererParams, content: React.ReactNode) => (
          <LockedOtherAttributeCell
            columnContext={columnContext}
            isLocked={attribute.locked === true}
            params={params}
          >
            {content}
          </LockedOtherAttributeCell>
        );
        let customParams: {};
        switch (attribute.typeOfValue) {
          case "MULTILINESTRING":
            customParams = {
              filter: "agTextColumnFilter",
              cellEditor: "agLargeTextCellEditor",
              cellEditorPopup: true,
              minWidth: 200,
              cellRenderer: (params) => renderLockedCell(params, CellEditFormatter(params.value)),
              editable: editableWhenUnlocked,
              autoHeight: true,
            };
            break;
          case "NUMBER":
            customParams = {
              filter: "agNumberColumnFilter",
              cellEditor: "agNumberCellEditor",
              valueGetter: (params) => {
                const val = params.data.otherAttributesData?.[attribute.key];
                if (Array.isArray(val) || val == null || val === "") return null;
                const num = Number(val);
                return Number.isNaN(num) ? null : num;
              },
              valueFormatter: (params) => formatNumberNb(params.value),
              valueSetter: (params) => {
                const newValue = params.newValue;
                if (Array.isArray(newValue)) return false;

                const cleaned = String(newValue)
                  .replace(/[\s\u00A0\u202F]/g, "")
                  .replace(",", ".");
                const num = Number(cleaned);

                if (Number.isNaN(num)) return false;

                params.data.otherAttributesData = {
                  ...params.data.otherAttributesData,
                  [attribute.key]: num,
                };
                return true;
              },
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, formatNumberNb(params.value)),
            };
            break;
          case "BOOLEAN":
            customParams = {
              filter: "agFilter",
              editable: false,
              valueGetter: (params) =>
                params.data.otherAttributesData?.[attribute.key] === true
                  ? "Ja"
                  : params.data.otherAttributesData?.[attribute.key] === false
                  ? "Nei"
                  : "",
              cellRenderer: (params) => (
                <LockedOtherAttributeCell
                  columnContext={columnContext}
                  isLocked={attribute.locked === true}
                  params={params}
                >
                  <SimpleStampCheck
                    disabled={!isLockedAttributeCellEditable(params, columnContext, attribute.locked === true)}
                    toggled={params.value == "" ? null : params.value === "Ja"}
                    toggle={async (val) => {
                      await columnContext.handleUpdateIssueField(params.data._id, params.colDef.field, val);
                      if (attribute.locked === true) {
                        columnContext.lockOtherAttributeCell(params.data._id, params.colDef.field);
                      }
                    }}
                  />
                </LockedOtherAttributeCell>
              ),
            };
            break;
          case "DROPDOWN":
            const options = otherAttributesConfig?.[attribute.key]?.tableOptions ?? [];
            const isNumberDropdown = otherAttributesConfig?.[attribute.key]?.dropdownOptionType === "NUMBER";
            const OptionWithColor = (params: ValueFormatterParams) => {
              const values = Array.isArray(params.value) ? params.value : [params.value];

              return (
                <div className="d-flex flex-wrap">
                  {values.map((value, index) => {
                    const foundOption = options.find((option) => String(option.value) === String(value));
                    return (
                      <div key={index} className="d-flex align-items-center mr-2">
                        {foundOption?.color && (
                          <div className="statusdot" style={{ backgroundColor: foundOption?.color }} />
                        )}
                        <div>
                          {isNumberDropdown && Number.isFinite(Number(value)) ? (
                            <NumberFormatter value={Number(value)} thousandSeparator=" " decimalSeparator="," />
                          ) : (
                            String(value ?? "")
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            };
            const formatDropdownValue = (value: unknown) => {
              if (Array.isArray(value)) {
                return value.map((item) => (isNumberDropdown ? formatNumberNb(item) : String(item ?? ""))).join(", ");
              }

              return isNumberDropdown ? formatNumberNb(value) : String(value ?? "");
            };
            customParams = {
              filter: "agFilter",
              cellEditor: "agRichSelectCellEditor",
              editable: editableWhenUnlocked,
              valueFormatter: (params) => formatDropdownValue(params.value),
              valueSetter: (params) => params.newValue,
              cellEditorParams: {
                values: options.map((opt) => opt.value),
                cellRenderer: OptionWithColor,
                valueListMaxHeight: 220,
                multiSelect: otherAttributesConfig?.[attribute.key]?.allowMultipleSelectionsInDropdown === true,
              },
              cellRenderer: (params) => renderLockedCell(params, <OptionWithColor {...params} />),
            };
            break;
          case "DATE":
            customParams = {
              filter: "agDateColumnFilter",
              cellEditor: "agDateStringCellEditor",
              valueFormatter: dateExportFormatter,
              valueGetter: (params) => params.data.otherAttributesData?.[attribute?.key] ?? "",
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, dateExportFormatter(params)),
            };
            break;
          case "FORMULA":
            customParams = {
              editable: false,
              filter: "agTextColumnFilter",
              minWidth: 200,
            };
            break;
          case "MATH_FORMULA":
            customParams = {
              editable: false,
              filter: "agTextColumnFilter",
              minWidth: 200,
              valueFormatter: (params) => formatNumberNb(params.value),
            };
            break;
          default:
            customParams = {
              filter: "agTextColumnFilter",
              editable: editableWhenUnlocked,
              cellRenderer: (params) => renderLockedCell(params, CellEditFormatter(params.value)),
            };
            break;
        }

        const otherAttributeColumn: ColDef = {
          field: `otherAttributesData.${attribute.key}`,
          headerName: attribute.label,
          autoHeaderHeight: true,
          headerComponentParams: {
            template: customAttributeHeaderTemplate(
              attribute.labelColor,
              attribute.locked,
              attribute.typeOfValue === "FORMULA" || attribute.typeOfValue === "MATH_FORMULA",
              attribute.typeOfValue === "MATH_FORMULA",
            ),
          },
          editable: editableWhenUnlocked,
          ...customParams,
          maxWidth: 300,
          context: sectionTitle,
          cellClass: "overflow-hidden px-1",
          ...(isTaskColumns !== true ? columnContext.hideAndSort(`otherAttributesData.${attribute.key}`) : {}),
        };
        otherAttributeColumns.push(otherAttributeColumn);
      });
  });

  return otherAttributeColumns;
};

const customAttributeHeaderTemplate = (
  color: string,
  isLocked: boolean,
  isFormula: boolean,
  isMathFormula: boolean,
) => `<div class="ag-cell-label-container py-0" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          ${
            isLocked === true
              ? `<span class="mr-1" title="Feltet er låst. Lås opp i cellen før redigering." style="cursor: default;" onclick="event.stopPropagation(); event.preventDefault();" onmousedown="event.stopPropagation();"><i class="fa fa-lock"></i></span>`
              : ""
          }
          ${color != null && color.length > 0 ? `<div class="mmi-dot mr-1" style="background-color: ${color};"></div>` : ""}
          ${
            isFormula
              ? `<div class="mr-1 text-muted" title="Formelfelt" style="font-weight: 600; font-size: 12px;">Æ’x</div>`
              : ""
          }
          ${
            isMathFormula
              ? `<div class="mr-1" title="Beta-funksjon" style="font-weight: 700; font-size: 10px; color: #8a6d3b; background: #fcf8e3; border: 1px solid #faebcc; border-radius: 10px; padding: 0 6px; line-height: 16px;">BETA</div>`
              : ""
          }
          <span data-ref="eText" class="ag-header-cell-text three-line-text-truncate" role="columnheader"></span>
          <div class="d-flex align-items-baseline" style="flex-direction: row;"></div>
          <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
      </div>`;
